class PredictiveSearch extends HTMLElement {
  constructor() {
    super();

    this.input = this.querySelector('input[type="search"]');
    this.predictiveSearchResults = this.querySelector('#predictive-search');
    this.suggestionsPanel = this.querySelector('#predictive-search-suggestions');
    this.clearButton = this.querySelector('#predictive-search-clear');
    this.backButton = this.querySelector('#predictive-search-back');
    this.form = this.querySelector('form');

    // Voor de mobiele fullscreen-weergave: het element dat position:fixed krijgt,
    // en de rij ernaast (logo/badge/cart/account) die dan verborgen wordt.
    this.headerSearchWrapper = this.closest('.header-search');
    this.headerMiddleBlock = this.headerSearchWrapper
      ? this.headerSearchWrapper.closest('.header-middle-block')
      : null;
    // De volledige koptekst-balk (zwart, met het R2B-beeldmerk) — deze wordt
    // tijdens fullscreen-zoeken zelf ook op position:fixed;top:0 gezet (zie CSS),
    // zodat hij altijd bovenaan het scherm blijft staan, ook als de pagina op het
    // moment van klikken al naar beneden gescrold was. Zonder dit werd er soms
    // paginainhoud (bijv. een hero-banner) zichtbaar boven de zoekfunctie, omdat
    // de balk dan nog op zijn oude, weggescrolde plek in de pagina stond.
    this.siteHeader = this.headerMiddleBlock
      ? this.headerMiddleBlock.closest('.site-header')
      : null;

    this.input.addEventListener('input', this.debounce((event) => {
      this.onChange(event);
    }, 300).bind(this));

    this.input.addEventListener('focus', () => {
      this.enterFullscreenIfMobile();
      this.onFocus();
    });

    if (this.clearButton) {
      this.clearButton.addEventListener('click', (event) => {
        event.preventDefault();
        this.onClear();
      });
    }

    if (this.backButton) {
      this.backButton.addEventListener('click', (event) => {
        event.preventDefault();
        this.onBack();
      });
    }

    // Sluit de dropdown/suggesties zodra er ergens buiten de zoekbalk geklikt wordt.
    this.boundOnDocumentClick = this.onDocumentClick.bind(this);
    document.addEventListener('click', this.boundOnDocumentClick);

    window.addEventListener('resize', this.debounce(() => {
      this.syncWidth();
    }, 100).bind(this));

    this.syncWidth();
    this.updateClearButton();
  }

  onClear() {
    this.input.value = '';
    this.updateClearButton();
    this.close();
    this.input.focus();
  }

  // Terugknop (alleen zichtbaar op mobiel in fullscreen-modus): sluit de
  // zoekfunctie volledig en geeft de normale header terug.
  onBack() {
    this.input.value = '';
    this.updateClearButton();
    this.close();
    this.exitFullscreen();
    this.input.blur();
  }

  isMobile() {
    return window.matchMedia('(max-width: 767px)').matches;
  }

  enterFullscreenIfMobile() {
    if (!this.isMobile() || !this.headerSearchWrapper) return;
    this.headerSearchWrapper.classList.add('predictive-search--fullscreen');
    if (this.headerMiddleBlock) this.headerMiddleBlock.classList.add('predictive-search-open');
    document.body.classList.add('predictive-search-open');

    // Wacht twee frames tot cart/account/taalwissel/hamburger/topbalk echt
    // verborgen zijn en de layout opnieuw is berekend, zodat we de definitieve
    // hoogte van de overgebleven kopbalk (logo) meten — daar plakken we de
    // zoekfunctie precies onder. Eén frame bleek soms nog een tussenstand te
    // meten (het logo werd dan half overlapt), vandaar dubbel.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        // Meet de balk die nu daadwerkelijk position:fixed;top:0 heeft (de hele
        // .site-header, zwart met het R2B-logo) — niet .header-middle-block zelf,
        // want die staat nog gewoon in de normale paginaflow en zijn positie zegt
        // dus niets over waar hij straks (na de fixed-CSS) op het scherm komt.
        const pinnedBar = this.siteHeader || this.headerMiddleBlock;
        if (pinnedBar) {
          const rect = pinnedBar.getBoundingClientRect();
          // Bewust een paar pixels EERDER laten beginnen dan gemeten (overlapBuffer),
          // in plaats van precies op de gemeten hoogte. Meet deze balk net iets te
          // laag in (bijv. door een style/berekening die niet exact overeenkomt met
          // wat er zichtbaar zwart is), dan bleef er anders een dun randje over
          // waar geen van beide elementen iets tekent — daar piepte paginainhoud
          // doorheen. Door bewust iets te overlappen dekt de zwarte balk (die een
          // hoger z-index heeft) dat randje altijd af, in plaats van dat er een
          // gat ontstaat.
          const overlapBuffer = 12;
          this.headerSearchWrapper.style.top = Math.max(rect.bottom - overlapBuffer, 0) + 'px';
        }
        this.syncWidth();
      });
    });
  }

  exitFullscreen() {
    if (this.headerSearchWrapper) {
      this.headerSearchWrapper.classList.remove('predictive-search--fullscreen');
      this.headerSearchWrapper.style.top = '';
    }
    if (this.headerMiddleBlock) this.headerMiddleBlock.classList.remove('predictive-search-open');
    document.body.classList.remove('predictive-search-open');
  }

  // Toont het kruisje alleen als er daadwerkelijk een zoekterm is ingevuld.
  updateClearButton() {
    if (!this.clearButton) return;
    const hasValue = this.input.value.trim().length > 0;
    this.clearButton.classList.toggle('is-visible', hasValue);
  }

  onDocumentClick(event) {
    if (!this.contains(event.target)) {
      this.close();
    }
  }

  onFocus() {
    const searchTerm = this.input.value.trim();

    if (!searchTerm.length) {
      // Nog niks getypt: toon de handmatig ingestelde zoeksuggesties (5 stuks,
      // in te vullen bij de header-instellingen) in plaats van de AJAX-resultaten.
      this.showSuggestions();
      return;
    }

    this.getSearchResults(searchTerm);
  }

  onChange() {
    const searchTerm = this.input.value.trim();
    this.updateClearButton();

    if (!searchTerm.length) {
      this.showSuggestions();
      return;
    }

    this.getSearchResults(searchTerm);
  }

  getSearchResults(searchTerm) {
    // resources[type] bepaalt welke resultaattypes worden opgehaald. Zonder dit
    // param haalt Shopify standaard alleen queries, producten, collecties en
    // pagina's op — artikelen (nodig voor het "Advies"-blok) moeten expliciet
    // aangevraagd worden. Het aantal getoonde producten/artikelen wordt beperkt
    // in sections/predictive-search.liquid zelf (via de limit: filter). resources[limit]
    // wordt hier bewust op het maximum (10) gezet: de "Zoeksuggesties" in
    // predictive-search.liquid worden opgebouwd uit de types/titels van ALLE
    // opgehaalde producten (niet alleen de 3 die getoond worden), zodat er ook bij
    // zoektermen met maar 1 of 2 categorieën genoeg matches zijn om altijd 5
    // suggesties te kunnen tonen.
    fetch(`/search/suggest?q=${searchTerm}&resources[type]=product,query,article&resources[limit]=10&section_id=predictive-search`)
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser().parseFromString(text, 'text/html').querySelector('#shopify-section-predictive-search').innerHTML;
        this.predictiveSearchResults.innerHTML = resultsMarkup;
        this.hideSuggestions();
        this.open();
        // De koppen ("Zoeksuggesties" bovenaan) vielen boven de zichtbare rand
        // zodra het paneel (of, op mobiel in fullscreen-modus, de omliggende
        // .header-search.predictive-search--fullscreen met overflow-y:auto) nog
        // gescrold stond van een eerdere, langere weergave — bijvoorbeeld nadat
        // iemand door het aanbevolen-paneel had gescrold vóór het typen. De
        // nieuwe resultaten vervingen dan wel de inhoud, maar niet de
        // scrollpositie van die omliggende container, waardoor het paneel leek
        // te openen "al een stukje naar beneden gescrold" met de eerste kop
        // erboven afgesneden. Daarom hier expliciet terug naar boven scrollen
        // zodra er nieuwe resultaten binnenkomen.
        this.resetScroll();
      })
      .catch((error) => {
        this.close();
        throw error;
      });
  }

  // Scrolt het resultatenpaneel zelf en (op mobiel, in fullscreen-modus) de
  // omliggende scrollbare wrapper terug naar boven, zodat de eerste kop
  // ("Zoeksuggesties") altijd zichtbaar is zodra er nieuwe inhoud verschijnt.
  resetScroll() {
    if (this.predictiveSearchResults) this.predictiveSearchResults.scrollTop = 0;
    if (this.headerSearchWrapper) this.headerSearchWrapper.scrollTop = 0;
  }

  showSuggestions() {
    this.syncWidth();
    if (this.predictiveSearchResults) this.predictiveSearchResults.style.display = 'none';
    if (this.suggestionsPanel) this.suggestionsPanel.style.display = 'block';
    this.resetScroll();
  }

  hideSuggestions() {
    if (this.suggestionsPanel) this.suggestionsPanel.style.display = 'none';
  }

  // Zorgt dat de dropdown exact zo breed is als het zoekveld zelf, niet het
  // hele zoekveld + de knop samen. Wordt opnieuw berekend bij openen en bij
  // het resizen van het venster, zodat het altijd klopt.
  // Uitzondering: in de mobiele fullscreen-weergave staat er ook nog een
  // terugknop vóór het invoerveld, dus daar laten we de resultaten gewoon de
  // volle breedte van de balk gebruiken in plaats van alleen de inputbreedte.
  syncWidth() {
    if (!this.input || !this.form) return;

    this.form.style.position = this.form.style.position || 'relative';

    const isFullscreen = this.headerSearchWrapper
      && this.headerSearchWrapper.classList.contains('predictive-search--fullscreen');

    if (isFullscreen) {
      // In fullscreen-modus zijn dit gewone paginablokken (position: static via CSS),
      // dus eventuele eerder gezette inline positionering weer weghalen.
      [this.predictiveSearchResults, this.suggestionsPanel].forEach((el) => {
        if (!el) return;
        el.style.position = '';
        el.style.left = '';
        el.style.width = '';
        el.style.top = '';
      });
      return;
    }

    const inputRect = this.input.getBoundingClientRect();
    const formRect = this.form.getBoundingClientRect();
    const left = (inputRect.left - formRect.left) + 'px';
    const width = inputRect.width + 'px';

    [this.predictiveSearchResults, this.suggestionsPanel].forEach((el) => {
      if (!el) return;
      el.style.position = 'absolute';
      el.style.boxSizing = 'border-box';
      el.style.left = left;
      el.style.width = width;
      el.style.top = '100%';
    });
  }

  open() {
    this.syncWidth();
    this.predictiveSearchResults.style.display = 'block';
  }

  close() {
    if (this.predictiveSearchResults) this.predictiveSearchResults.style.display = 'none';
    this.hideSuggestions();
  }

  debounce(fn, wait) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  }
}

customElements.define('predictive-search', PredictiveSearch);